import { useMemo } from 'react';
import { ArrowUpDown, Trash2, StretchHorizontal, TrendingDown } from 'lucide-react';
import {
  usePlanEditor, STAIRS_KINDS, STAIRS_DIRECTIONS, stairsKindFor, stairsDirectionFor,
} from './PlanEditorContext';
import { FoldableSection } from './FoldableSection';

/**
 * StairsSkewPanels — V14.69 Phases 1 & 2.
 * Sections du panneau droit (sub-tab Murs) pour :
 *   • Escaliers : direction (monte/descend/les deux), type, suppression.
 *     La direction pilote la duplication auto de l'emprise sur l'étage lié.
 *   • Lignes de brisure (mansardes) : HSP côté bas + suppression.
 */
export const StairsPanelSection = ({ disabled }) => {
  const { stairs, selectedStairsId, setSelectedStairsId, actions } = usePlanEditor();
  if (!stairs || stairs.length === 0) return null;
  return (
    <FoldableSection
      title={`Escaliers · ${stairs.length}`}
      icon={StretchHorizontal}
      iconClass="text-purple-300"
      testid="stairs-panel-section"
    >
      <div className="mt-1.5 space-y-1">
        {stairs.map((st) => {
          const isSel = st.id === selectedStairsId;
          const isMirror = st.source === 'mirror';
          return (
            <div key={st.id}
                 className={`rounded border px-2 py-1.5 text-[11px] cursor-pointer transition-colors ${
                   isSel ? 'border-purple-400/60 bg-purple-500/10' : 'border-white/10 hover:bg-white/[0.04]'
                 }`}
                 onClick={() => setSelectedStairsId(isSel ? null : st.id)}
                 data-testid={`stairs-row-${st.id}`}>
              <div className="flex items-center gap-1.5">
                <span className="text-purple-300 font-medium">{stairsKindFor(st.kind).label}</span>
                <span className="text-gray-400">{stairsDirectionFor(st.direction).glyph} {stairsDirectionFor(st.direction).label}</span>
                {isMirror && (
                  <span className="text-[9px] px-1 py-0.5 rounded bg-slate-500/20 text-slate-300 border border-slate-400/30">
                    Copie étage lié
                  </span>
                )}
                <button type="button"
                        className="ml-auto text-rose-300/70 hover:text-rose-300"
                        onClick={(e) => { e.stopPropagation(); actions.deleteStairs(st.id); }}
                        disabled={disabled}
                        title="Supprimer l'escalier"
                        data-testid={`stairs-delete-${st.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              {isSel && !isMirror && (
                <div className="mt-1.5 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    <ArrowUpDown className="w-3 h-3 text-gray-400" />
                    {STAIRS_DIRECTIONS.map((d) => (
                      <button key={d.value} type="button"
                              onClick={() => actions.updateStairs(st.id, { direction: d.value })}
                              disabled={disabled}
                              className={`px-1.5 h-6 rounded text-[10px] border transition-colors ${
                                (st.direction || 'up') === d.value
                                  ? 'bg-purple-500/20 border-purple-400/50 text-purple-200'
                                  : 'border-white/10 text-gray-400 hover:bg-white/5'
                              }`}
                              data-testid={`stairs-dir-${d.value}-${st.id}`}>
                        {d.glyph} {d.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    {STAIRS_KINDS.map((k) => (
                      <button key={k.value} type="button"
                              onClick={() => actions.updateStairs(st.id, { kind: k.value })}
                              disabled={disabled}
                              className={`px-1.5 h-6 rounded text-[10px] border transition-colors ${
                                st.kind === k.value
                                  ? 'bg-purple-500/20 border-purple-400/50 text-purple-200'
                                  : 'border-white/10 text-gray-400 hover:bg-white/5'
                              }`}
                              data-testid={`stairs-kind-${k.value}-${st.id}`}>
                        {k.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[9.5px] text-gray-500 leading-snug">
                    Déplacez l'escalier par glisser-déposer · poignée coin bas-droit pour la taille.
                    La direction duplique l'emprise sur l'étage lié automatiquement.
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </FoldableSection>
  );
};

export const SkewPanelSection = ({ disabled }) => {
  const { skewEdges, selectedSkewId, setSelectedSkewId, actions, calibration } = usePlanEditor();
  const scale = calibration?.scale_m_per_norm_unit || null;
  const lengths = useMemo(() => {
    const m = {};
    (skewEdges || []).forEach((se) => {
      const d = Math.sqrt((se.x2 - se.x1) ** 2 + (se.y2 - se.y1) ** 2);
      m[se.id] = scale ? `${(d * scale).toFixed(2)} m` : `${(d * 100).toFixed(0)}%`;
    });
    return m;
  }, [skewEdges, scale]);
  if (!skewEdges || skewEdges.length === 0) return null;
  return (
    <FoldableSection
      title={`Brisures mansarde · ${skewEdges.length}`}
      icon={TrendingDown}
      iconClass="text-amber-300"
      testid="skew-panel-section"
    >
      <div className="mt-1.5 space-y-1">
        {skewEdges.map((se) => {
          const isSel = se.id === selectedSkewId;
          return (
            <div key={se.id}
                 className={`rounded border px-2 py-1.5 text-[11px] cursor-pointer transition-colors ${
                   isSel ? 'border-amber-400/60 bg-amber-500/10' : 'border-white/10 hover:bg-white/[0.04]'
                 }`}
                 onClick={() => setSelectedSkewId(isSel ? null : se.id)}
                 data-testid={`skew-row-${se.id}`}>
              <div className="flex items-center gap-1.5">
                <span className="text-amber-300 font-medium">Brisure</span>
                <span className="text-gray-400">{lengths[se.id]}</span>
                <span className="text-gray-500">HSP basse {Number(se.low_side_hsp_m || 1.2).toFixed(2)} m</span>
                <button type="button"
                        className="ml-auto text-rose-300/70 hover:text-rose-300"
                        onClick={(e) => { e.stopPropagation(); actions.deleteSkewEdge(se.id); }}
                        disabled={disabled}
                        title="Supprimer la brisure"
                        data-testid={`skew-delete-${se.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              {isSel && (
                <div className="mt-1.5 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <label className="text-[10px] text-gray-400">HSP côté bas (m)</label>
                  <input
                    type="number" min="0.5" max="4" step="0.1"
                    value={Number(se.low_side_hsp_m || 1.2)}
                    onChange={(e) => actions.updateSkewEdge(se.id, {
                      low_side_hsp_m: Math.max(0.5, Math.min(4, parseFloat(e.target.value) || 1.2)),
                    })}
                    disabled={disabled}
                    className="w-16 h-6 px-1.5 rounded bg-white/5 border border-white/15 text-[11px] text-white"
                    data-testid={`skew-hsp-input-${se.id}`}
                  />
                  <span className="text-[9.5px] text-gray-500">Extrémités draggables sur le canvas</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </FoldableSection>
  );
};
